import React from 'react';
import Icon from '../../../components/AppIcon';

const ErrorFilterPanel = ({ filters, onFilterChange, statistics }) => {
  const errorTypes = [
    { value: '', label: 'All Types' },
    { value: 'api_error', label: 'API Error' },
    { value: 'database_error', label: 'Database Error' },
    { value: 'user_action_failure', label: 'User Action Failure' },
    { value: 'command_failure', label: 'Command Failure' },
    { value: 'validation_error', label: 'Validation Error' },
    { value: 'authentication_error', label: 'Authentication Error' },
    { value: 'authorization_error', label: 'Authorization Error' },
    { value: 'network_error', label: 'Network Error' },
    { value: 'timeout_error', label: 'Timeout Error' },
    { value: 'integration_error', label: 'Integration Error' }
  ];

  const severityLevels = [
    { value: '', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'new', label: 'New' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'investigating', label: 'Investigating' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'auto_recovered', label: 'Auto-Recovered' },
    { value: 'failed_recovery', label: 'Failed Recovery' }
  ];

  const handleChange = (field, value) => {
    onFilterChange({ [field]: value });
  };

  const clearFilters = () => {
    onFilterChange({
      errorType: '',
      severity: '',
      errorStatus: '',
      affectedModule: '',
      searchTerm: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-neutral-900">Filters</h3>
        <button
          onClick={clearFilters}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          Clear All
        </button>
      </div>

      <div className="space-y-4">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Search
          </label>
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={filters?.searchTerm || ''}
              onChange={(e) => handleChange('searchTerm', e?.target?.value)}
              placeholder="Search errors..."
              className="w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Error Type */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Error Type
          </label>
          <select
            value={filters?.errorType || ''}
            onChange={(e) => handleChange('errorType', e?.target?.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {errorTypes?.map((type) => (
              <option key={type?.value} value={type?.value}>
                {type?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Severity */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Severity
          </label>
          <select
            value={filters?.severity || ''}
            onChange={(e) => handleChange('severity', e?.target?.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {severityLevels?.map((level) => (
              <option key={level?.value} value={level?.value}>
                {level?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Status
          </label>
          <select
            value={filters?.errorStatus || ''}
            onChange={(e) => handleChange('errorStatus', e?.target?.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {statusOptions?.map((status) => (
              <option key={status?.value} value={status?.value}>
                {status?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Affected Module */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Affected Module
          </label>
          <input
            type="text"
            value={filters?.affectedModule || ''}
            onChange={(e) => handleChange('affectedModule', e?.target?.value)}
            placeholder="e.g., payroll, compliance"
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Date From
          </label>
          <input
            type="datetime-local"
            value={filters?.dateFrom || ''}
            onChange={(e) => handleChange('dateFrom', e?.target?.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Date To
          </label>
          <input
            type="datetime-local"
            value={filters?.dateTo || ''}
            onChange={(e) => handleChange('dateTo', e?.target?.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Statistics Summary */}
      {statistics && (
        <div className="mt-6 pt-6 border-t border-neutral-200">
          <h4 className="text-sm font-semibold text-neutral-900 mb-3">Error Distribution</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-600">Critical</span>
              <span className="font-medium text-red-600">{statistics?.bySeverity?.critical || 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-600">High</span>
              <span className="font-medium text-orange-600">{statistics?.bySeverity?.high || 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-600">Medium</span>
              <span className="font-medium text-yellow-600">{statistics?.bySeverity?.medium || 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-600">Low</span>
              <span className="font-medium text-blue-600">{statistics?.bySeverity?.low || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorFilterPanel;
